import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { requestTypes, currencies } from "../../../utilities/RequestData";
import {
  addPaymentRequest,
  fetchPaymentRequests,
} from "../../../../contents/redux/actions/paymentRequestActions";
import { fetchPaymentProjects } from "../../../../contents/redux/actions/paymentProjectActions";
import { fetchPaymentPurposes } from "../../../../contents/redux/actions/paymentPurposeActions";
import {
  WorkRounded,
  MonetizationOnRounded,
  PaymentsRounded,
  LiveHelpRounded,
  EditRounded,
  AttachFileRounded,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import styles from "../../../utilities/cssmodules/PaymentRequest.module.css";

const PaymentRequest = () => {
  const location = useLocation();
  const { requestTypeId } = location.state || {};
  const userId = localStorage.getItem("userId");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [inputs, setInputs] = useState({
    project: "",
    amount: "",
    currency: "",
    purpose: "",
    description: "",
    supportingDocuments: null,
  });

  // Redux state
  const {
    paymentProjects,
    loading: projectsLoading,
    error: projectsError,
  } = useSelector((state) => state.paymentProjects || {});
  const {
    paymentPurposes,
    isLoading: purposesLoading,
    isError: purposesError,
  } = useSelector((state) => state.paymentPurposes || {});
  const addError = useSelector(
    (state) => state.requests?.errors?.addPaymentRequest || null,
  );

  // Debug logs
  useEffect(() => {
    console.log("PaymentRequest - State:", {
      requestTypeId,
      userId,
      paymentProjects,
      paymentPurposes,
      addError,
      supportingDocuments: inputs.supportingDocuments
        ? inputs.supportingDocuments.name
        : null,
    });
  }, [
    requestTypeId,
    userId,
    paymentProjects,
    paymentPurposes,
    addError,
    inputs.supportingDocuments,
  ]);

  // Fetch projects and purposes
  useEffect(() => {
    dispatch(fetchPaymentProjects());
    dispatch(fetchPaymentPurposes());
  }, [dispatch]);

  // Handle input changes for text fields
  const handleChange = (event) => {
    const { name, value } = event.target;
    setInputs((prev) => ({ ...prev, [name]: value }));
  };

  // Handle file input change
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    setInputs((prev) => ({ ...prev, supportingDocuments: file }));
  };

  // Validate and submit form (Save or Submit)
  const handleFormSubmission = (e, isOnSaved) => {
    e.preventDefault();
    if (!requestTypeId) {
      toast.error("Request Type ID is missing.");
      return;
    }
    if (!userId) {
      toast.error("User ID is missing. Please log in again.");
      return;
    }
    if (
      !inputs.project ||
      !inputs.amount ||
      !inputs.currency ||
      !inputs.purpose ||
      !inputs.description
    ) {
      toast.error("Please fill in all required fields.");
      return;
    }

    // Create FormData for file upload
    const formData = new FormData();
    formData.append("RequestTypeId", parseInt(requestTypeId));
    formData.append("UserId", parseInt(userId));
    formData.append("ProjectId", parseInt(inputs.project));
    formData.append("Amount", parseFloat(inputs.amount));
    formData.append("Currency", inputs.currency);
    formData.append("PurposeId", parseInt(inputs.purpose));
    formData.append("Description", inputs.description);
    formData.append("IsOnSaved", isOnSaved);
    if (inputs.supportingDocuments) {
      formData.append("SupportingDocuments", inputs.supportingDocuments);
    }

    console.log("Submitting payment request:", Object.fromEntries(formData));
    dispatch(addPaymentRequest(formData))
      .then(() => {
        navigate("/myrequests");
        dispatch(fetchPaymentRequests());
        setInputs({
          project: "",
          amount: "",
          currency: "",
          purpose: "",
          description: "",
          supportingDocuments: null,
        });
        toast.success(
          `Payment request ${isOnSaved ? "saved" : "submitted"} successfully.`,
        );
      })
      .catch((error) => {
        console.error("Submission error:", error);
        toast.error(
          `Failed to ${isOnSaved ? "save" : "submit"} payment request.`,
        );
      });
  };

  const handleSave = (e) => handleFormSubmission(e, true);
  const handleSubmit = (e) => handleFormSubmission(e, false);

  return (
    <div className={styles["component-wrapper"]}>
      <div className={styles["component-title"]}>Payment Request</div>
      <div className={styles["generic-activity-component-description"]}>
        Create a new payment request by filling out the form below and
        submitting when you are done. Ensure all fields have accurate
        information.
      </div>
      {addError && <div className={styles["error-message"]}>{addError}</div>}
      {projectsLoading && (
        <div className={styles["loading-message"]}>Loading projects...</div>
      )}
      {purposesLoading && (
        <div className={styles["loading-message"]}>
          Loading payment purposes...
        </div>
      )}
      <div className={styles["generic_request_content_component_wrapper"]}>
        <form
          className={styles["generic-activity-component-form"]}
          onSubmit={handleSubmit}
          encType="multipart/form-data"
        >
          <label className={styles["form-label"]}>
            <WorkRounded /> Project
          </label>
          <section className={styles["form-section"]}>
            <select
              name="project"
              required
              value={inputs.project}
              onChange={handleChange}
              className={styles["form-select"]}
              aria-label="Project"
            >
              <option value="">Select a project</option>
              {Array.isArray(paymentProjects) &&
                paymentProjects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
            </select>
            {projectsError && (
              <span className={styles["error"]}>Error loading projects</span>
            )}
          </section>

          <label className={styles["form-label"]}>
            <MonetizationOnRounded /> Amount
          </label>
          <section className={styles["form-section"]}>
            <input
              type="number"
              name="amount"
              placeholder="Enter amount"
              required
              value={inputs.amount}
              onChange={handleChange}
              className={styles["form-input"]}
              aria-label="Amount"
            />
          </section>

          <label className={styles["form-label"]}>
            <PaymentsRounded /> Currency
          </label>
          <section className={styles["form-section"]}>
            <select
              name="currency"
              required
              value={inputs.currency}
              onChange={handleChange}
              className={styles["form-select"]}
              aria-label="Currency"
            >
              <option value="">Select a currency</option>
              {currencies.map((currency) => (
                <option key={currency.id} value={currency.name}>
                  {currency.name}
                </option>
              ))}
            </select>
          </section>

          <label className={styles["form-label"]}>
            <LiveHelpRounded /> Purpose
          </label>
          <section className={styles["form-section"]}>
            <select
              name="purpose"
              required
              value={inputs.purpose}
              onChange={handleChange}
              className={styles["form-select"]}
              aria-label="Purpose"
            >
              <option value="">Select a purpose</option>
              {Array.isArray(paymentPurposes) &&
                paymentPurposes.map((purpose) => (
                  <option key={purpose.id} value={purpose.id}>
                    {purpose.name}
                  </option>
                ))}
            </select>
            {purposesError && (
              <span className={styles["error"]}>Error loading purposes</span>
            )}
          </section>

          <label className={styles["form-label"]}>
            <EditRounded /> Description
          </label>
          <section className={styles["form-section"]}>
            <textarea
              name="description"
              placeholder="Enter description"
              required
              value={inputs.description}
              onChange={handleChange}
              className={styles["form-textarea"]}
              aria-label="Description"
            />
          </section>

          <label className={styles["form-label"]}>
            <AttachFileRounded /> Supporting Documents
          </label>
          <section className={styles["form-section"]}>
            <input
              type="file"
              name="supportingDocuments"
              accept="application/pdf,image/*"
              onChange={handleFileChange}
              className={styles["form-file"]}
              aria-label="Supporting Documents"
            />
            {inputs.supportingDocuments && (
              <span className={styles["file-name"]}>
                {inputs.supportingDocuments.name}
              </span>
            )}
          </section>

          <div className={styles["button-row"]}>
            <button
              type="button"
              className={styles["generic-save-btn"]}
              onClick={handleSave}
              disabled={
                !inputs.project ||
                !inputs.amount ||
                !inputs.currency ||
                !inputs.purpose ||
                !inputs.description ||
                projectsLoading ||
                purposesLoading
              }
              aria-label="Save payment request"
            >
              Save
            </button>
            <button
              type="button"
              className={styles["generic-submit-btn"]}
              onClick={handleSubmit}
              disabled={
                !inputs.project ||
                !inputs.amount ||
                !inputs.currency ||
                !inputs.purpose ||
                !inputs.description ||
                projectsLoading ||
                purposesLoading
              }
              aria-label="Submit payment request"
            >
              Submit
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PaymentRequest;
